import { useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import { Mail, AlertCircle } from 'lucide-react'
import { Modal } from '@/shared/components/ui/Modal'
import { Button } from '@/shared/components/ui/Button'
import { api } from '@/shared/api/axios'
import { getApiErrorMessage } from '@/shared/utils/apiError'

// Real email send for a Quotation/Proforma Invoice/Tax Invoice - the
// backend (POST /invoice-requests/:id/send-email) renders the document's
// already-stored HTML to a PDF and sends it via the sender's own SMTP
// settings (Settings > Email Settings). Self-contained so it can be
// dropped onto any document list/view without needing to understand that
// page's own state.

interface SendDocumentEmailModalProps {
  open: boolean
  onClose: () => void
  documentId: string
  documentNumber: string
  requestType: 'quotation' | 'proforma' | 'invoice'
  clientEmail?: string
  clientName?: string
  invoiceApprovedBy?: string
  /** Called once the email has gone out, before the modal closes. */
  onSent?: () => void
}

const DOC_LABEL: Record<string, string> = {
  quotation: 'Quotation',
  proforma: 'Proforma Invoice',
  invoice: 'Tax Invoice',
}

export function SendDocumentEmailModal({
  open,
  onClose,
  documentId,
  documentNumber,
  requestType,
  clientEmail,
  clientName,
  invoiceApprovedBy,
  onSent,
}: SendDocumentEmailModalProps) {
  const docLabel = DOC_LABEL[requestType] || 'Document'
  const [to, setTo] = useState(clientEmail || '')
  const [cc, setCc] = useState('')
  const [subject, setSubject] = useState(`${docLabel} ${documentNumber}${clientName ? ` for ${clientName}` : ''}`)
  const [message, setMessage] = useState('')
  const [sending, setSending] = useState(false)

  // Tax Invoices can be sent by their owner too, just not before approval -
  // mirrors the same gate the backend enforces, so the button is disabled
  // with an explanation rather than letting the request fail silently.
  const blockedByApproval = requestType === 'invoice' && !invoiceApprovedBy

  const handleSend = async () => {
    if (!to.trim()) {
      toast.error('Enter a recipient email address')
      return
    }
    setSending(true)
    try {
      await api.post(
        `/invoice-requests/${documentId}/send-email`,
        {
          to: to.trim(),
          cc: cc.trim() ? cc.split(',').map((s) => s.trim()).filter(Boolean) : undefined,
          subject: subject.trim(),
          message: message.trim() || undefined,
        },
        { skipErrorToast: true }
      )
      toast.success(`${docLabel} sent to ${to.trim()}`)
      onSent?.()
      onClose()
    } catch (err) {
      toast.error(getApiErrorMessage(err, `send this ${docLabel.toLowerCase()}`))
    } finally {
      setSending(false)
    }
  }

  const footer = blockedByApproval ? (
    <Button variant="outline" onClick={onClose}>
      Close
    </Button>
  ) : (
    <>
      <Button variant="outline" onClick={onClose} disabled={sending}>
        Cancel
      </Button>
      <Button onClick={handleSend} disabled={sending}>
        <Mail className="h-3.5 w-3.5 mr-1.5" />
        {sending ? 'Sending...' : 'Send'}
      </Button>
    </>
  )

  return (
    <Modal open={open} onClose={onClose} title={`Send ${docLabel} by Email`} size="md" footer={footer}>
      {blockedByApproval ? (
        <div className="flex items-start gap-2.5 p-3 rounded-lg border border-amber-200 bg-amber-50 text-amber-800 dark:bg-amber-950/30 dark:border-amber-800 dark:text-amber-300 text-sm">
          <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
          <p>This tax invoice hasn't been approved yet. It can be sent once approval is complete.</p>
        </div>
      ) : (
        <div className="space-y-3.5">
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-gray-700 dark:text-gray-300">To</label>
            <input
              type="email"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="client@example.com"
              className="w-full h-9 px-3 text-sm rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-gray-700 dark:text-gray-300">CC (optional, comma-separated)</label>
            <input
              type="text"
              value={cc}
              onChange={(e) => setCc(e.target.value)}
              placeholder="manager@company.com, accounts@company.com"
              className="w-full h-9 px-3 text-sm rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-gray-700 dark:text-gray-300">Subject</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full h-9 px-3 text-sm rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-gray-700 dark:text-gray-300">Message (optional)</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              placeholder={`Please find attached ${docLabel} ${documentNumber}.`}
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none"
            />
          </div>
          <p className="text-[11px] text-gray-500 dark:text-gray-400">
            Sent from your own mailbox - configure it under{' '}
            <Link to="/app/settings/email" className="text-primary underline">
              Settings → Email Settings
            </Link>{' '}
            if you haven't yet.
          </p>
        </div>
      )}
    </Modal>
  )
}
